import { useTranslation } from "@refinedev/core";
import { AttendantTemplate } from "components/attendantTemplate";
import { CollapsibleSection } from "components/collapsibleSection";
import {
  IAttendantTemplateInputItem,
  InputType,
  IPickupAttractionParticipantDetail,
  IPickupAttractionParticipantForm,
} from "interfaces";
import { get } from "lodash";
import { useEffect } from "react";
import { useFormContext } from "react-hook-form";
import { calculateAge } from "utills/helpers";

export const EditRegistrationInformationSection = ({
  participantDetailData,
  template,
}: {
  participantDetailData: IPickupAttractionParticipantDetail;
  template: IPickupAttractionParticipantDetail["attendant_template"];
}) => {
  const { translate } = useTranslation();

  const {
    register,
    reset,
    control,
    watch,
    getFieldState,
    formState: { errors },
  } = useFormContext<IPickupAttractionParticipantForm>(); // retrieve all hook methods

  useEffect(() => {
    if (template) {
      _resetFields(template);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [template]);

  const _resetFields = (
    attendantTemplate: IPickupAttractionParticipantDetail["attendant_template"],
  ) => {
    const result: any = {};

    for (const section in attendantTemplate) {
      if (attendantTemplate[section] && attendantTemplate[section].fields) {
        attendantTemplate[section].fields.forEach((field) => {
          if (field.key) {
            if (field.type === InputType.SELECT) {
              if (
                (field?.option ?? {}).hasOwnProperty(
                  participantDetailData?.participant[field.key],
                )
              ) {
                result[field.key] =
                  participantDetailData?.participant[field.key] ?? "";
              } else {
                result[field.key] = "";
              }
            } else if (
              field.type === InputType.MULTI_SELECT ||
              field.type === InputType.RADIO_BUTTON
            ) {
              const validOption: string[] = [];
              (field?.option ?? []).forEach(
                (opt: { Key: string; Value: any }[]) => {
                  if (
                    (participantDetailData?.participant[field.key] ?? "")
                      .split(",")
                      .includes(get(opt[0], "Value", ""))
                  ) {
                    validOption.push(get(opt[0], "Value", ""));
                  }
                },
              );
              result[field.key] = validOption.join(",") ?? "";
            } else {
              result[field.key] =
                participantDetailData?.participant[field.key] ?? "";
            }
            if (field.key.includes("phone") && field.validation === "phone") {
              result[`${field.key}_area_code`] =
                participantDetailData?.participant[`${field.key}_area_code`] ??
                "";
            }
          }
        });
      }
    }
    reset((prev) => ({ ...prev, ...result }));
    return result;
  };

  const isShowLegalField = (field: IAttendantTemplateInputItem) => {
    if (
      !field.key.includes("legal") &&
      !field.key.includes("runner_relationship")
    )
      return true;
    const dob = getFieldState("dob") ? (watch("dob") ?? null) : null;
    if (!dob) return false;
    let dobBy = null;
    for (const section in template) {
      if (template[section] && template[section].fields) {
        const dobField = template[section].fields.find(
          (field) => field.key === "dob",
        );
        if (dobField) {
          dobBy = get(dobField, "option.by", null);
          break;
        }
      }
    }
    return calculateAge(
      dob,
      dobBy || new Date(participantDetailData?.event?.start_date ?? ""),
    ) >= 18
      ? false
      : true;
  };

  return (
    <CollapsibleSection
      label={translate("registration-information")}
      defaultOpen={true}
    >
      <AttendantTemplate
        control={control}
        errors={errors}
        register={register}
        template={template}
        isShowLegalField={isShowLegalField}
      />
    </CollapsibleSection>
  );
};
